<template>
  <v-row class="text-left full-height">
    <v-col cols="12">
      <v-toolbar dark color="brand" rounded="0" flat dense>
        <v-btn icon dark @click="handleBack">
          <v-icon>{{
            showBackButton || editingSlot !== null ? "mdi-chevron-left" : "mdi-close"
          }}</v-icon>
        </v-btn>
        <v-toolbar-title class="triple-header-title"> Triple Component </v-toolbar-title>
      </v-toolbar>

      <!-- ============ VIEW 1: Slot selection ============ -->
      <div v-if="editingSlot === null" class="triple-content-area triple-scroll-area">
        <div class="triple-fields">
          <div v-if="programName" class="program-section">
            <div class="program-label">Program</div>
            <div class="program-name-text">{{ programName }}</div>
            <div class="program-date-text">{{ programDateLabel }}</div>
          </div>

          <v-divider class="my-0" />

          <v-switch
            v-model="sameHeight"
            @change="handleSameHeightChange"
            color="brandCyan"
            label="Make all components the same height"
            class="mt-4"
            hide-details
          />

          <!-- Audience targeting (top-section rows only) -->
          <AudienceGroupSelector
            v-if="!row.programId"
            :clientId="clientId || globalClientId"
            :audienceGroups="audienceGroups"
            @update:audienceGroups="handleAudienceChange"
            data-testid="audience-selector"
          />

          <div class="preview-section">
            <div class="preview-label">Preview</div>
            <div class="preview-subtitle">Select a component to edit</div>
          </div>

          <div class="slot-cards-row" :class="{ 'slot-cards-row--same-height': sameHeight }">
            <div v-for="(column, i) in Columns" :key="i" class="slot-card-wrapper">
              <v-menu :disabled="!column.DashboardWidgetType" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="column.DashboardWidgetType ? on : {}"
                    class="slot-card"
                    :class="{ 'slot-card--filled': !!column.DashboardWidgetType }"
                    @click="!column.DashboardWidgetType && handleSlotClick(i)"
                  >
                    <div
                      v-if="
                        column.DashboardWidgetType &&
                        column.DashboardWidgetType.type === 'wallet-balance'
                      "
                      class="slot-preview"
                    >
                      <div class="slot-preview__scale-content">
                        <Wallet :config="column.config" :previewMode="true" />
                      </div>
                    </div>
                    <div
                      v-else-if="
                        column.DashboardWidgetType && column.DashboardWidgetType.type === 'content'
                      "
                      class="slot-preview"
                    >
                      <div class="slot-preview__scale-content">
                        <Content :config="previewConfigForContent(column)" :previewMode="true" />
                      </div>
                    </div>
                    <div
                      v-else-if="
                        column.DashboardWidgetType &&
                        column.DashboardWidgetType.type === 'leaderboard'
                      "
                      class="slot-preview"
                    >
                      <div class="slot-preview__scale-content">
                        <span class="slot-card-label">{{ column.DashboardWidgetType.name }}</span>
                      </div>
                    </div>
                    <div
                      v-else-if="
                        column.DashboardWidgetType &&
                        column.DashboardWidgetType.type === 'aggregate-learning-leaderboard'
                      "
                      class="slot-preview"
                    >
                      <div class="slot-preview__scale-content">
                        <AggregateLearningLeaderboardMinimizedView
                          :config="column.config || {}"
                          :total-participants="MOCK_LEADERBOARD.totalParticipants"
                          :rows="MOCK_LEADERBOARD.rows"
                          :previewMode="true"
                        />
                      </div>
                    </div>
                    <div
                      v-else-if="
                        column.DashboardWidgetType && column.DashboardWidgetType.type === 'claims'
                      "
                      class="slot-preview"
                    >
                      <!-- Distinct class from scale-content so the #187389 count-of-4 guard stays green. -->
                      <div class="slot-preview__scale-thumb">
                        <Claim :config="column.config || {}" :previewMode="true" />
                      </div>
                    </div>
                    <span v-else class="slot-card-label">Add Component</span>
                  </div>
                </template>
                <v-list dense>
                  <v-list-item
                    v-if="
                      column.DashboardWidgetType &&
                      (column.DashboardWidgetType.type === 'content' ||
                        column.DashboardWidgetType.type === 'leaderboard' ||
                        column.DashboardWidgetType.type === 'aggregate-learning-leaderboard')
                    "
                    @click="handleSlotClick(i)"
                  >
                    <v-list-item-icon class="mr-2"
                      ><v-icon small>mdi-pencil</v-icon></v-list-item-icon
                    >
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="column.columnId || column.dashboardColumnId"
                    @click="handleDeleteWidget(column.columnId || column.dashboardColumnId, i)"
                  >
                    <v-list-item-icon class="mr-2"
                      ><v-icon small>mdi-delete</v-icon></v-list-item-icon
                    >
                    <v-list-item-title>Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ VIEW 2: Slot editor ============ -->
      <div v-else class="slot-editor pa-6 triple-scroll-area">
        <v-select
          v-if="!editingExistingLeaderboard"
          v-model="selectedWidgetType"
          :items="componentTypeOptions"
          item-text="text"
          item-value="value"
          label="Component Type"
          outlined
          dense
          color="brandCyan"
          class="mb-2"
        />

        <!-- ---- Content editor (delegated to ContentCreator) ---- -->
        <ContentCreator
          v-if="selectedWidgetType === 'content'"
          aria-label="Image and Text Editor"
          :row="effectiveRow"
          :column="columns[editingSlot]"
          :widgetTypes="widgetTypes"
          :rowCount="rowCount"
          :clientId="clientId"
          :showToolbar="false"
          :position="editingSlot + 1"
          :globalDefaults="globalDefaults"
          @saved="handleContentSaved"
          @close="handleContentClose"
          @refresh="refresh"
        />

        <!-- ---- Leaderboard editor (delegated to LeaderboardCreator) ---- -->
        <LeaderboardCreator
          v-else-if="selectedWidgetType === 'leaderboard'"
          :row="effectiveRow"
          :column="columns[editingSlot]"
          :widgetTypes="widgetTypes"
          :rowCount="rowCount"
          :position="editingSlot + 1"
          @saved="handleLeaderboardSaved"
          @close="handleLeaderboardClose"
          @refresh="refresh"
          @switch-to-content="selectedWidgetType = 'content'"
        />

        <!-- ---- Aggregate Learning Leaderboard editor (delegated to AggregateLearningLeaderboardCreator) ---- -->
        <AggregateLearningLeaderboardCreator
          v-else-if="selectedWidgetType === 'aggregate-learning-leaderboard'"
          :row="effectiveRow"
          :column="columns[editingSlot]"
          :widgetTypes="widgetTypes"
          :rowCount="rowCount"
          :position="editingSlot + 1"
          @saved="handleAggregateLearningLeaderboardSaved"
          @close="handleAggregateLearningLeaderboardClose"
          @refresh="refresh"
        />

        <!-- ---- Wallet (no editor — auto-inserted) ---- -->
        <div v-else-if="selectedWidgetType === 'wallet-balance'" class="text-center py-6">
          <v-progress-circular v-if="loading.general" indeterminate color="brandCyan" />
          <p v-else>Wallet component will be inserted.</p>
        </div>

        <p v-if="error.general" class="error--text text-center mt-4">{{ error.general }}</p>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import ProgramService from "@/services/ProgramService";
import LeaderboardCreator from "@/components/account-management/dashboard/LeaderboardCreator.vue";
import AggregateLearningLeaderboardCreator from "@/components/account-management/dashboard/AggregateLearningLeaderboardCreator.vue";
import ContentCreator from "@/components/account-management/dashboard/ContentCreator.vue";
import Wallet from "@/components/program/participant/dashboard/Wallet.vue";
import Content from "@/components/program/participant/dashboard/Content.vue";
import Claim from "@/components/program/participant/dashboard/Claim.vue";
import AggregateLearningLeaderboardMinimizedView from "@/components/aggregate-learning-leaderboard/AggregateLearningLeaderboardMinimizedView.vue";
import { MOCK_LEADERBOARD } from "@/components/aggregate-learning-leaderboard/mockData";
import AudienceGroupSelector from "@/components/account-management/dashboard/AudienceGroupSelector.vue";

import { mapState } from "vuex";

function initialState() {
  return {
    sameHeight: true,
    columns: [{}, {}, {}],
    editingSlot: null,
    selectedWidgetType: null,
    form: { valid: true },
    error: { general: null },
    loading: { general: false, widgetTypes: false },
    rowId: null,
    audienceGroups: null,
  };
}

export default {
  name: "TripleComponentCreator",
  props: {
    programName: {
      type: String,
      default: null,
    },
    row: {
      type: Object,
      required: true,
    },
    widgetTypes: {
      type: Array,
      default: () => [],
    },
    rowCount: {
      type: Number,
      default: 0,
    },
    showBackButton: { type: Boolean, default: true },
    clientId: { type: [Number, String], default: null },
    globalDefaults: { type: Object, default: () => ({}) },
  },
  components: {
    LeaderboardCreator,
    AggregateLearningLeaderboardCreator,
    ContentCreator,
    Wallet,
    Content,
    Claim,
    AggregateLearningLeaderboardMinimizedView,
    AudienceGroupSelector,
  },
  data() {
    return initialState();
  },
  created() {
    this.preloadRow();
  },
  methods: {
    // --- Navigation ---
    handleBack() {
      if (this.editingSlot !== null) {
        // Editor view → go back to slots
        this.editingSlot = null;
        this.selectedWidgetType = null;
        this.error.general = null;
      } else {
        // Slots view → close sidesheet
        this.$emit("close");
      }
    },
    // --- Slot selection ---
    handleSlotClick(slotIndex) {
      this.editingSlot = slotIndex;
      this.error.general = null;
      const column = this.columns[slotIndex];
      if (column && (column.columnId ?? column.dashboardColumnId)) {
        this.selectedWidgetType = column.DashboardWidgetType?.type || "content";
      } else {
        this.selectedWidgetType = "content";
      }
    },
    // --- Delegated editor events ---
    captureRowId(rowId) {
      if (rowId && !this.rowId) this.rowId = rowId;
    },
    handleLeaderboardSaved({ columnId, config, rowId }) {
      this.captureRowId(rowId);
      const widgetType = this.widgetTypes?.find((wt) => wt.type === "leaderboard");
      this.$set(this.columns, this.editingSlot, {
        columnId,
        config,
        DashboardWidgetType: widgetType,
        position: this.editingSlot + 1,
      });
      this.editingSlot = null;
      this.selectedWidgetType = null;
    },
    handleLeaderboardClose() {
      this.editingSlot = null;
      this.selectedWidgetType = null;
      this.error.general = null;
    },
    handleAggregateLearningLeaderboardSaved({ columnId, config, rowId }) {
      this.captureRowId(rowId);
      const widgetType = this.widgetTypes?.find(
        (wt) => wt.type === "aggregate-learning-leaderboard"
      );
      this.$set(this.columns, this.editingSlot, {
        columnId,
        config,
        DashboardWidgetType: widgetType,
        position: this.editingSlot + 1,
      });
      this.editingSlot = null;
      this.selectedWidgetType = null;
    },
    handleAggregateLearningLeaderboardClose() {
      this.editingSlot = null;
      this.selectedWidgetType = null;
      this.error.general = null;
    },
    handleContentSaved({ columnId, config, rowId }) {
      this.captureRowId(rowId);
      const widgetType = this.widgetTypes?.find((wt) => wt.type === "content");
      this.$set(this.columns, this.editingSlot, {
        columnId,
        config,
        DashboardWidgetType: widgetType,
        position: this.editingSlot + 1,
      });
      this.editingSlot = null;
      this.selectedWidgetType = null;
    },
    handleContentClose() {
      this.editingSlot = null;
      this.selectedWidgetType = null;
      this.error.general = null;
    },
    // --- Slots view save (sameHeight) ---
    handleSameHeightChange() {
      // Update local column configs immediately
      const savedColumns = this.columns.filter((c) => c && c.columnId);
      savedColumns.forEach((col) => {
        if (!col.config) col.config = {};
        if (!col.config.rowMeta) col.config.rowMeta = {};
        col.config.rowMeta.sameHeight = this.sameHeight;
      });
      // Persist to backend and refresh parent data
      if (savedColumns.length > 0) {
        Promise.all(
          savedColumns.map((col) =>
            ProgramService.updateDashboardColumn(col.columnId, {
              config: col.config,
            }).catch(() => {})
          )
        ).then(() => {
          this.$emit("refresh");
        });
      }
    },
    async handleAudienceChange(value) {
      this.audienceGroups = value;
      // Toggle ON with no group selected — do not persist the invalid empty state.
      if (Array.isArray(value) && value.length === 0) {
        return;
      }
      const rowId = this.row?.rowId || this.rowId;
      if (rowId) {
        try {
          await ProgramService.updateDashboardRow(rowId, { audienceGroups: value });
          this.$emit("refresh");
        } catch (e) {
          this.error.general = "Failed to save audience targeting";
        }
      }
    },
    async handleSave() {
      if (this.loading.general) return;
      this.loading.general = true;
      try {
        for (const col of this.columns.filter((c) => c && c.columnId)) {
          await ProgramService.updateDashboardColumn(col.columnId, {
            config: { ...col.config, rowMeta: { sameHeight: this.sameHeight } },
          });
        }
        this.$emit("saved", { rowId: this.row.rowId, sameHeight: this.sameHeight });
      } catch (err) {
        this.error.general = err.message || "Failed to save";
      } finally {
        this.loading.general = false;
      }
    },

    async handleDeleteWidget(columnId, index) {
      try {
        await ProgramService.deleteDashboardColumn(columnId);
        this.$set(this.columns, index, {});
        this.$emit("refresh");
      } catch (err) {
        this.error.general = err.message || "Failed to delete";
      }
    },
    refresh() {
      this.$emit("refresh");
    },
    // Slot-card preview only: `column.config` is the raw save payload from
    // ContentCreator.buildConfig(), which intentionally sets
    // background.color/gradient to null when the admin never touched the
    // color picker (so the widget keeps dynamically inheriting whatever the
    // global default is, rather than baking in today's value forever — see
    // ADR-0001 / nullCoalesceMergeConfig). Content.vue's backgroundStyle only
    // applies an inline color when config.background.color/gradient is
    // truthy, so a freshly-created (or freshly-edited-without-touching-color)
    // component renders with NO background here until the row is re-fetched
    // from the server (which resolves the null to the actual current global
    // default at read time). This resolves the same fallback locally for the
    // preview only — it does not mutate `column.config` or affect what gets
    // persisted.
    previewConfigForContent(column) {
      const config = column?.config || {};
      const hasColor = !!config.background?.color;
      const hasGradient = !!config.background?.gradient;
      if (hasColor || hasGradient) return config;
      if (this.globalDefaults?.cardBackgroundColor) {
        return {
          ...config,
          background: { ...config.background, color: this.globalDefaults.cardBackgroundColor },
        };
      }
      return config;
    },
    async insertWalletColumn() {
      this.error.general = null;
      this.loading.general = true;
      try {
        let rowId = this.row?.rowId || this.rowId;
        if (!rowId) {
          const rowData = {
            rowTypeId: this.row.rowTypeId,
            programId: this.row.programId,
            position: this.rowCount + 1,
          };
          const clientId = this.clientId || this.globalClientId;
          if (clientId) rowData.clientId = clientId;
          const rowResponse = await ProgramService.createDashboardRow(rowData);
          rowId = rowResponse.data.result.rowId;
          this.rowId = rowId;
        }

        const widgetType = this.widgetTypes?.find((wt) => wt.type === "wallet-balance");
        const colResponse = await ProgramService.createDashboardColumn({
          rowId,
          widgetTypeId: widgetType?.widgetTypeId,
          config: {},
          position: this.editingSlot + 1,
          clientId: this.clientId || this.globalClientId,
        });
        const columnId = colResponse.data.result.columnId;

        this.$set(this.columns, this.editingSlot, {
          columnId,
          config: {},
          DashboardWidgetType: widgetType,
          position: this.editingSlot + 1,
        });

        this.editingSlot = null;
        this.selectedWidgetType = null;
        this.$emit("refresh");
      } catch (err) {
        this.error.general = err.message || "Failed to insert wallet";
      } finally {
        this.loading.general = false;
      }
    },
    resetPage() {
      Object.assign(this.$data, initialState());
    },
    preloadRow() {
      this.resetPage();
      this.audienceGroups = this.row?.audienceGroups ?? null;
      if (this.row) {
        const columns = this.row?.DashboardColumns || [];
        if (!columns?.length) {
          this.columns = [{}, {}, {}];
          return;
        }
        this.columns = [...columns]
          .sort((a, b) => a.position - b.position)
          .map((col) => ({
            ...col,
            columnId: col.columnId ?? col.dashboardColumnId,
          }));
        for (var colIndex = 1; colIndex <= 3; colIndex++) {
          const col = this.columns.find((c) => c.position === colIndex);
          if (!col) {
            this.columns.splice(colIndex - 1, 0, {});
          }
        }
        const existingCol = this.columns.find((c) => c.config?.rowMeta?.sameHeight !== undefined);
        this.sameHeight = existingCol?.config?.rowMeta?.sameHeight ?? true;
      }
    },
  },
  computed: {
    ...mapState(["userProfile", "clients", "globalClientId"]),
    effectiveRow() {
      if (this.rowId && !this.row?.rowId) {
        return { ...this.row, rowId: this.rowId };
      }
      return this.row;
    },
    Columns() {
      return this.columns?.slice(0, 3) || [];
    },
    programDateLabel() {
      const program = this.row?.Program;
      if (!program) return "";
      const fmt = (d) => {
        if (!d) return null;
        return new Date(d).toLocaleDateString("en-US", {
          month: "numeric",
          day: "numeric",
          year: "2-digit",
        });
      };
      const start = fmt(program.startDate);
      const end = fmt(program.endDate);
      if (!start && !end) return "";
      if (start && !end) return start;
      return `${start} – ${end}`;
    },
    componentTypeOptions() {
      const types = this.widgetTypes || [];
      const isTopSection = !this.row?.programId;
      const isLearning = !!this.row?.isLearning;

      // TODO: only have aggregate learning leaderboard as option with feature flag
      let allowedTypes;
      if (isTopSection) {
        allowedTypes = ["content", "wallet-balance", "aggregate-learning-leaderboard"];
      } else if (isLearning) {
        allowedTypes = ["content", "leaderboard"];
      } else {
        allowedTypes = ["content"];
      }

      const options = types
        .filter((wt) => allowedTypes.includes(wt.type))
        .map((wt) => ({
          text: wt.name,
          value: wt.type,
        }));

      return options;
    },
    editingExistingLeaderboard() {
      if (this.editingSlot === null) return false;
      const column = this.columns[this.editingSlot];
      return !!(
        column &&
        column.columnId &&
        (column.DashboardWidgetType?.type === "leaderboard" ||
          column.DashboardWidgetType?.type === "aggregate-learning-leaderboard")
      );
    },
    MOCK_LEADERBOARD() {
      return MOCK_LEADERBOARD;
    },
  },
  watch: {
    row: {
      handler() {
        this.preloadRow();
      },
    },
    selectedWidgetType(newVal) {
      if (newVal === "wallet-balance") {
        this.insertWalletColumn();
      }
    },
  },
};
</script>

<style scoped>
.row-type-container {
  border: 1px solid var(--v-brandPrimary-base);
  padding: 10px;
  border-radius: 8px;
  /* font-weight: bold; */
}

.triple-header-title {
  font-family: "Avenir", sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 28px;
}

.triple-content-area {
  padding: 16px 24px 8px;
}

.triple-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0;
}

.program-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.program-label {
  font-family: "Avenir", sans-serif;
  font-weight: 800;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.87);
}

.program-name-text {
  font-family: "Avenir", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.87);
}

.program-date-text {
  font-family: "Avenir", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.87);
}

.preview-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.preview-label {
  font-family: "Avenir", sans-serif;
  font-weight: 800;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.87);
}

.preview-subtitle {
  font-family: "Avenir", sans-serif;
  font-weight: 350;
  font-size: 14px;
  line-height: 22px;
  color: rgba(0, 0, 0, 0.87);
}

.slot-cards-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
}

.slot-cards-row--same-height {
  align-items: stretch;
}

.slot-cards-row--same-height .slot-card-wrapper {
  display: flex;
}

.slot-cards-row--same-height .slot-card {
  flex: 1;
}

.slot-card-wrapper {
  flex: 1;
  min-width: 0;
}

.slot-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  min-height: 96px;
  padding: 8px 6px;
  background-color: #f6f5f6;
  border: 0.35px solid rgba(36, 55, 62, 0.12);
  border-radius: 3px;
  box-shadow: 0px 1.4px 4.2px 0px rgba(205, 208, 210, 0.24);
  cursor: pointer;
}

.slot-card--filled {
  background-color: #ffffff;
  border: none;
  border-radius: 8px;
  box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14),
    0px 1px 3px 0px rgba(0, 0, 0, 0.12);
  padding: 0;
  height: auto;
  min-height: 0;
  justify-content: flex-start;
}

.slot-preview {
  width: 100%;
  overflow: hidden;
  border-radius: 3px;
  position: relative;
  pointer-events: none;
}

.slot-preview__scale-content {
  /* zoom shrinks content to a thumbnail; width compensates so the shrunk
     content still fills its .slot-preview container instead of leaving a
     gap. If this zoom factor ever changes, update the calc() divisor to
     match — the two values must stay in sync. */
  zoom: 0.333;
  width: calc(100% / 0.333);
}

.slot-preview__scale-thumb {
  /* zoom/width must stay in sync with .slot-preview__scale-content above. */
  zoom: 0.333;
  width: calc(100% / 0.333);
}

.slot-card:hover {
  background-color: #eeedee;
}

.slot-card-label {
  font-family: "Avenir", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
}

.triple-scroll-area {
  overflow-y: auto;
  max-height: calc(100vh - 48px);
}

/* Editor view */
.slot-editor {
}

.section-title {
  font-family: "Avenir", sans-serif;
  font-weight: 800;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.87);
}

.color-swatch {
  width: 40px;
  height: 40px;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
}

.color-swatch-sm {
  width: 32px;
  height: 32px;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
}
</style>
